import React, { useRef } from "react";
import { GRID_SLIDER_MAX, IMAGE_SLIDER_MAX_SIZE, LIST_SLIDER_MAX } from "../../../utils/constants";
import { getConfig, makeAssetLink } from "../../../utils/helpers";
import ReactImageGallery from "react-image-gallery";
import { SliderLeftNav, SliderRightNav } from "./sliderNavButtons";

/** **ItemImageSlider** - Image slider component for List / Grid vehicle cards
 * 
 * @param {{
 *  images: string[], 
 *  imageContainerClassName?: string,
 *  imageContainerStyles: React.CSSProperties,
 *  imageClassName: string,
 *  imageStyles: React.CSSProperties, 
 *  onClick:()=>void, 
 *  imgHeight?: number,
 *  imgWidth?: number,
 *  viewScreen?: 'home' | 'search' | 'recommended',
 *  viewType: 'grid' | 'list',
 * }} props 
 * @returns 
 */
function ItemImageSlider ({
    images=[], 
    imageContainerClassName='',
    imageContainerStyles={}, // cannot be used in ReactImageGallery
    imageStyles={}, // cannot be used in ReactImageGallery
    imageClassName='',
    imgHeight,
    imgWidth,
    onClick,
    viewScreen,
    viewType='grid',
    
}) {
    // Get config for max image count
    const sliderImageMaxSize = determineMaxImageCount(viewType);
    const galleryRef = useRef(null);
    const galleryContainerRef = useRef(null);
    const placeholderImg = makeAssetLink('placeholder.png');
    const imageCssClass = makeImageClassName(imageClassName, viewScreen)
 
    /** Replace 'http' with 'https' | maps to expected obj shape for gallery
     * 
     * @param {string} image 
     * @param {number} index 
     * @returns {{original: string}[]}
     */
    const mapImageToGalleryItems = (image, index) => {
        const imageItem = {
            original: image ? image.replace(/^http:\/\//i, 'https://') : '',
            originalClass: imageCssClass,
        };
        if (imgHeight) imageItem.originalHeight = imgHeight;
        if (imgWidth) imageItem.originalWidth = imgWidth;
        return imageItem;
    }

    /* Image URLS - limit the amount of images */
    const imageSrcList = [];
    try {
        if (sliderImageMaxSize && images.length && (images.length > sliderImageMaxSize)) {
            imageSrcList.push(
                ...[...images.slice(0, sliderImageMaxSize)]
                .map(mapImageToGalleryItems)
            );
        } else {
            imageSrcList.push(...[...images].map(mapImageToGalleryItems));
        }
    //    devLogImageDiff(images, imageSrcList);
    
    } catch(sliderImageLimitError) {
        console.error(sliderImageLimitError?.message);
        if (images instanceof Array) {
            imageSrcList.push(...[...images].map(mapImageToGalleryItems));
        }
    }

    /* Autoplay -- *disabled* for use in Slider (i.e. 'react-slick') */
    /*  Slider - 'play' */
    const galleryPlay = () => galleryRef.current?.play();
    /*  Slider - 'pause' */
    const galleryPause = () => galleryRef.current?.pause();
    
    return (
        <div
            className="srp-image-slider-container"
            ref={galleryContainerRef}
        >
          <ReactImageGallery
            additionalClass={imageContainerClassName}
            items={imageSrcList}
            lazyLoad={true}
            onErrorImageURL={placeholderImg}
            onClick={onClick}
            ref={galleryRef}
            renderLeftNav={(onNavClick, disabled) => (
                <SliderLeftNav 
                    disabled={disabled} 
                    onClick={e => {
                        // e.stopPropagation();
                        e.target && onNavClick(e);
                    }} 
                />
            )}
            renderRightNav={(onNavClick, disabled) => (
                <SliderRightNav 
                    onClick={e => {
                        // e.stopPropagation();
                        e.target && onNavClick();
                    }}
                    disabled={disabled} 
                />
            )}
            showBullets={false}
            showThumbnails={false}
            showPlayButton={false}
            showNav={true}
            showFullscreenButton={false}
            slideInterval={2000}
            useBrowserFullscreen={false}
          />
        </div>
    )

}

/**
 * 
 * @param {'grid' | 'list'} viewType 
 */
function determineMaxImageCount (viewType='grid') {
    let sliderImageCount = 5;
    switch (viewType) {
        case 'grid':
            const configGridImgMax = getConfig('srpGridMaxImages');
            sliderImageCount = (configGridImgMax && typeof configGridImgMax === 'number')
                ? configGridImgMax
                : GRID_SLIDER_MAX;
            break;

        case 'list':
            const configListImgMax = getConfig('srpListMaxImages');
            sliderImageCount = (configListImgMax && typeof configListImgMax === 'number')
                ? configListImgMax
                : LIST_SLIDER_MAX;
            break;

        default: 
            sliderImageCount = IMAGE_SLIDER_MAX_SIZE;
            break;
    }
    return sliderImageCount;
}

/** Determines additional `<ReactImageGallery>` props based on `viewScreen` (_i.e. the page it will be rendered_)
 * 
 * _**Note: Unused at the moment**_
 * 
 * @param {'home' | 'recommended' | 'search' | undefined} viewScreen 
 */
function getAdditionalOptions (viewScreen) {
    const additionalOptions = {};
    switch (viewScreen) {
        /* Home page swipe behaviors */
        case 'home':
            break;

        /* Recommended page swipe behaviors */
        case 'recommended':
            break;
        
        /* Search page swipe behaviors */
        case 'search':
            break;
        
        /* Defaults */
        default:
            break;
    }
    return additionalOptions;
}

/**
 * @param {string | undefined} imageClassName Image CSS class
 * @param {'home' | 'recommended' | 'search' | undefined} viewScreen 
 */
function makeImageClassName (imageClassName='', viewScreen) {
    let imageCssClass = imageClassName;
    switch (viewScreen) {
        /* Home page img class name */
        case 'home':
            if (imageCssClass) {
                imageCssClass += ' ';
            }
            imageCssClass += 'image-gallery-image__home'
            break;

        /* Recommended page img class name */
        case 'recommended':
            break;
        
        /* Search page img class name */
        case 'search':
            break;
        
        /* Defaults */
        default:
            break;
    }
    // console.info(" * * * * * *", `. * * C L A S S * * ====> \n   ${imageCssClass}`)
    return imageCssClass;
}

function devLogImageDiff (images=[], newImages=[]) {
    console.info(
     ' * Images size change * ', 
     `  initial count  --> '${images.length}'`, 
     `  new count --> '${newImages.length}'`
    );
}

export {ItemImageSlider};